import { MARK_GROUPS, MEASURES, STATEMENTS, STYLE_NAMES } from './index';

/**
 * The construction language, as text a person or a model can be handed.
 *
 * In `domain` rather than beside the panel, because the *server* needs it too: the proxy that
 * holds the API key builds the prompt itself rather than accepting one from the browser, or the
 * endpoint would be an open relay to a paid API. Both sides therefore read the same reference,
 * and it is generated from the parser's own table so neither can be handed a stale one.
 */

/** The worked example the panel opens with, and the one the reference ends on. */
export const EXAMPLE_SCRIPT = `# Tam giác ABC và đường tròn ngoại tiếp
A = point 0 0
B = point 160 0
C = point 50 120

segment A B
segment B C
segment C A

O = circumcenter A B C
circle3 A B C

label A "A"
label B "B"
label C "C"
label O "O"`;

/**
 * The four solid statements, written out as scripts that run.
 *
 * Concrete rather than `prism ... size <width> <height> <depth>`, for two reasons. A model copies
 * a form it can see working and has to invent one it cannot, and inventing is exactly what went
 * wrong: shown no form at all, it guessed `prism A B C D A2 B2 C2 D2` — no `.` — every time.
 * And a runnable line can be *checked*, which is why these are a value and not prose:
 * `reference.test.ts` parses each one, so the reference cannot describe a syntax the parser
 * stopped accepting.
 */
export const SOLID_FORMS: readonly string[] = [
  'prism A B C D . A2 B2 C2 D2 at 0 0 size 240 220 240',
  'pyramid S . A B C D at 0 0 size 240 220 240 over A',
  'cone S O at 0 0 size 360 230',
  'cylinder O O2 at 0 0 size 360 230',
  'sphere O at 0 0 size 320',
];

/**
 * The two-view statements, as a script that runs.
 *
 * Here for the reason `SOLID_FORMS` is: both are handled in the parser rather than in
 * `STATEMENTS`, so the generated list cannot reach them, and a statement missing from the
 * reference may as well not exist. That is not hypothetical — it is exactly how all four solid
 * statements went undocumented while the reference went on describing the way solids were drawn
 * before they existed. `reference.test.ts` parses every line below.
 */
export const EPURE_FORMS: readonly string[] = [
  'ground 420',
  'epure A 60 90 120',
  'epure B 300 170 30',
  'vtrace M A B',
  'htrace N A B',
  'truelength T A B',
];

/**
 * The complete reference, as plain text.
 *
 * Generated from the same table the parser uses, so it cannot describe a statement that does
 * not exist or omit one that does. Handing a model a stale grammar is how you get a script
 * that fails on every line.
 */
export function grammarText(): string {
  const lines = [
    'EduSketch construction language.',
    'One statement per line. `#` starts a comment.',
    'Assign a name with `=` to refer to an object later: `A = point 0 0`.',
    'Coordinates are plain numbers; y increases upward.',
    '',
    'Statements:',
    ...STATEMENTS.map(
      (statement) =>
        `  ${statement.keyword} ${statement.parameters
          .map((parameter) => `<${parameter.name}>`)
          .join(' ')}  — ${statement.summary}`
    ),
    /*
     * The five that work on an object already built rather than building one.
     *
     * They live in the parser rather than in `STATEMENTS`, so the generated list above cannot
     * reach them — and a statement missing from the reference may as well not exist, for a
     * teacher reading it or a model being handed it. `label` was in that position for a while
     * and the module's own example used it.
     */
    '  label <object> "<text>"  — names an object; the name is drawn beside it.',
    `  style <object> <${Object.keys(STYLE_NAMES).join('|')}>  — how it is drawn.`,
    '  hide <object>  — keeps it out of the picture without deleting it.',
    `  marks <graph> [${Object.keys(MARK_GROUPS).join('] [')}]  — dots the notable points of a`,
    '  curve and writes each coordinate. With nothing after the name, all of them; add',
    '  `guides` to swap the coordinates for dashed lines to the axes with the value on each.',
    '  variation <graph>  — the bảng biến thiên of that curve, drawn under the figure.',
    '  region "<inequality>" ...  — shades the miền nghiệm of a system of linear',
    '  inequalities in `x` and `y`, and draws each boundary — dashed when strict.',
    '  All five take the *name* of an object, so give a line one before styling it:',
    '  `AH = segment A H`, then `style AH accent`. `style A H accent` is two objects.',
    '',
    /*
     * The solid statements, which the generated list above cannot reach either.
     *
     * This section used to describe the way solids were drawn before these existed: place every
     * corner yourself at coordinates you have projected by hand, draw the base with `oval`, and
     * mark the edges inside with `style ... hidden`. All three are now wrong, and a model handed
     * them does exactly what it is told — hand-placed pyramids with the dashes written in, which
     * then stay put when the figure is dragged. Being absent would have been better than that.
     */
    'Drawing a solid — `at x y`, then `size width height depth`:',
    '  The **middle** number is the upright one. A problem usually gives the floor first and the',
    '  height last — "sàn 4m x 4m, cao 3,2m" — and writing the three numbers in that order puts',
    '  the height where the depth goes: `size 200 200 160` is a room 200 tall on a 200x160 floor,',
    '  not a 200x200 floor 160 tall. That one is `size 200 160 200`.',
    ...SOLID_FORMS.map((form) => `  ${form}`),
    '  The `.` separates the apex, or the top ring, from the base, and a prism needs the',
    '  same number of names each side. A round solid has no corners and so no `.`, and',
    '  one measurement across rather than a width and a depth.',
    '  `hull A C B2 D2` is the **convex solid on points already in space** — a corner of a figure,',
    '  a midpoint, a point dividing an edge. The tetrahedron ACB2D2 of a cube is one line, and',
    '  what comes back is a body with faces: hidden edges, sections and shading all work on it.',
    '  Four points in one plane are a quadrilateral, not a solid, and are refused.',
    '  `cut p A` is the **piece a plane leaves**, named by a corner it keeps: "mặt phẳng (MNP)',
    '  chia khối chóp thành hai phần, phần chứa đỉnh S". A corner rather than a side, because a',
    '  plane has no up: its normal depends on the order its three points were named.',
    '  `sphere` takes one name — its centre — and one number, its diameter. It has no rim, so',
    '  `rim` does not apply to it: every great circle of a sphere is as good as every other.',
    '  It is drawn as an outline with its equator, and it dashes the far half of any line that',
    '  runs through it, which is what "mặt cầu ngoại tiếp" needs.',
    '  `circle d` adds **đường tròn giao tuyến** — the circle a plane at distance `d` from the',
    '  centre cuts out, drawn as a small ellipse across the ball with its far half dashed. The',
    '  distance is signed, so `circle -100` is below the centre and `circle 60` above, and the',
    '  clause repeats: two parallel planes are two circles. Give it a name — `circle H 60` — and',
    '  that name is **the circle and its centre at once**, as a cone base is: `H` is the foot of',
    '  the perpendicular from O, so `segment O H` is the distance and `rim H 210` is a point on',
    '  the circle. That is the right triangle OHA the question is solved with — `OH = d`,',
    '  `HA = √(R² − d²)`, `OA = R` — and it is how "tam giác ABC nội tiếp đường tròn giao tuyến"',
    '  gets its corners. The points come out on the sphere, so everything built from them is',
    '  worked out in space. `rim O 90` on the sphere itself still refuses: every great circle is',
    '  as good as every other, so an angle round "the" rim of a ball names nothing.',
    '  `circle H 60 tilt 30` leans the plane 30° off horizontal, turning the ring in the plane of',
    '  the page. Left out of nearly every figure — a plane "cách tâm O một khoảng d" is drawn',
    '  level — and there for the ones that are not. `H` follows the lean, staying the stated',
    '  distance from O and still perpendicular to its own circle. 90° stands the plane on edge,',
    '  where the ring is seen as a line rather than a circle, and is refused.',
    '  Its radius is **not** given — it is',
    '  √(R² − d²), the theorem the question is about, so a figure cannot state what the theorem',
    '  forbids. A plane that misses the ball or only touches it is refused.',
    '  `over A` is optional, comes last, and says what stands above what. On a pyramid it puts',
    '  the apex over that base corner — `SA ⊥ (ABCD)` — instead of over the centre. On a prism it',
    '  puts the **first top corner** there, which is what makes the prism oblique: for "hình chiếu',
    '  vuông góc của A\' lên (ABC) trùng với trung điểm H của BC" write `over B C`, because two',
    '  names mean the midpoint of the edge between them. `over A` on a prism is the upright one.',
    '  The whole top ring moves together, so every lateral edge stays the same length.',
    '  `at` takes two numbers, or the **name of a point already built**: `at 0 0`, or `at I`.',
    '  The second form is how a mặt cầu ngoại tiếp is drawn — the centre is worked out from the',
    '  solid (`I = midpoint S C`), and the sphere follows it when the solid changes.',
    '  `angle A 60` states the shape of a three- or four-cornered base: that angle at that',
    '  corner, so a right-angled triangle, a rhombus or a parallelogram. `right B` is the same',
    '  clause with the number filled in, for the commonest case of all.',
    '  The two sides meeting there are the size numbers: back to the previous corner is the',
    '  width, on to the next is the depth. So "đáy vuông tại B, AB = a, BC = a√3" is',
    '  `size a … a√3 right B`, and "đáy ABCD là hình thoi cạnh a, góc BAD = 60°" is',
    '  `size a … a angle A 60` — a rhombus is the case where the two are equal.',
    '  `base x1 z1 x2 z2 ...` states the floor corner by corner, for the shape no clause',
    '  describes — including a **dented** one: `base 0 0 240 0 240 90 90 90 90 240 0 240` is an',
    '  L-shaped floor, and the hidden edges are worked out for it like any other.',
    '  `trapezoid A 420` is the third of the family, for the base an angle cannot describe: the',
    '  two parallel sides differ, and 420 is the far one. `size a … a trapezoid A 2a` is',
    '  "đáy ABCD là hình thang vuông tại A và D, AB = a, CD = 2a, AD = a" — the side between',
    '  the parallels is perpendicular to both, so the right angles land at A and at D.',
    '  `isosceles A 420` is the same trapezium with the far side centred instead of flush, so',
    '  the two legs come out equal — "hình thang cân", which `trapezoid` cannot draw.',
    '  In both, the *near* parallel side is `depth` and the far one is this number, while',
    '  `width` is the side between them. "AB = a, CD = 2a, AD = a" is therefore',
    '  `size a … a trapezoid A 2a` — putting 2a in `depth` as well makes both parallels equal,',
    '  which is a parallelogram and is refused.',
    '  A trapezium may lean: `trapezoid A 420 60` puts 60° at the named corner. `isosceles`',
    '  may not — its legs are mirror images only while the axis is square to the parallels.',
    '  `base 0 0 240 0 300 150 70 210` gives the corners outright, for the shape no clause',
    '  describes. One pair of numbers per corner, in the base plane; `size` is then the height',
    '  alone, because the ring already says how wide and how deep. The base must be convex —',
    '  a dented one breaks the rule that decides which edges are hidden — and either direction',
    '  round is accepted, with the names left where they were written.',
    '  One clause per solid. Without any, three corners are an equilateral triangle and five or',
    '  more a regular polygon, and a regular shape has **one** measure across: width and depth',
    '  must then be equal, or the base is not the shape its caption claims. Four corners are the',
    '  exception, where the two numbers mean a rectangle.',
    '  For a regular base, `width` is the diameter of the circle the corners sit on, **not the',
    '  side**. The two differ, and for a triangle they differ a lot: an equilateral triangle on a',
    '  circle of diameter w has side w x √3/2, so "đáy là tam giác đều cạnh a" is',
    '  `size 1.155a … 1.155a` and not `size a … a`. Writing the side into `width` draws a base',
    '  13% too small, which is invisible on its own and wrong the moment the problem relates it',
    '  to anything: "tam giác đều cạnh a, AA\' = 2a" written `size 200 400 200` gives a lateral',
    '  edge 2.309 times the side rather than twice it. Four corners are again the exception —',
    '  there the numbers are the sides themselves.',
    '  These place and name every vertex themselves, and work out which of their own',
    '  edges they hide. Never give a corner coordinates, and never write',
    '  `style ... hidden`: the solid decides that, and it decides again when it moves.',
    '',
    'Hình hoạ — the two-view drawing (đồ thức):',
    ...EPURE_FORMS.map((form) => `  ${form}`),
    '  `ground` draws the projection axis `x`, from a short lead-in on the left to the length',
    '  given. `epure A 60 90 120` is the point A(along the axis, độ xa, độ cao), as **both** its',
    '  projections. It declares `A1` above the axis at the height, and `A2` below it at the',
    '  distance, joins them with the correspondence line, and labels both — so build on `A1` and',
    '  `A2` rather than on `A`: `A1B1 = segment A1 B1`.',
    '  Numbers are drawing units, as everywhere else; scale a problem in millimetres up.',
    '  `vtrace M A B` is the vết đứng of the line AB — the point of it whose độ xa is zero — and',
    '  `htrace N A B` the vết bằng, whose độ cao is zero. Each declares a point of the drawing',
    '  like any other, so `M1`, `M2` and the line tying them; what marks it as a trace is that',
    '  `M2` lands **on the axis**, and `N1` does. A line parallel to that plane has no trace',
    '  there and the statement says so rather than inventing one.',
    '  Both traces lie on the same line, so the figure a textbook draws is trace to trace:',
    '  `segment M1 N1` and `segment M2 N2` pass through A and B and reach the axis at each end.',
    '  `truelength T A B` builds the right triangle for độ dài thật on the hình chiếu bằng: the',
    '  difference in độ cao is raised at `B2`, the right angle is marked, and `A2T` **is** the',
    '  true length of AB — measure or caption it like any other segment.',
    '  Those are the constructions there are. Changing the plane of projection, rotation about',
    '  an axis, the third view and visibility between skew lines do not exist — a problem asking',
    '  for one cannot be drawn in full, and placing points to imitate the answer would draw a',
    '  diagram that is wrong with nothing to say so. Draw what can be drawn and say in a `#`',
    '  comment which step is missing.',
    '',
    'Points on and around a solid:',
    '  A **section** of a cone or cylinder through its axis is a triangle or a rectangle on a',
    '    diameter — and which diameter decides whether the figure says anything. `rim O 0` and',
    '    `rim O 180` are the front and the back, so that section is seen edge-on and comes out a',
    '    sliver. Take `rim O 90` and `rim O 270`, the left and right of the base, which project',
    '    to the full shape a book prints.',
    '  `intersect` works on the **page**. Two segments crossing in the drawing are recovered as a',
    '    point in space when their originals genuinely meet — so `O = intersect AC BD` is the',
    '    centre of a base, and `SO` drawn to it is dashed by the solid in front of it. Segments',
    '    that only cross in the picture stay page-bound, and lines drawn to that point cannot be',
    '    dashed: prefer `midpoint` or `divide` when either will say the same thing.',
    '  A = rim O 300  — a point on a round base, by angle in the base plane: 0 is the',
    '    front, and the angle grows anticlockwise seen from above. `O` must be a centre named',
    '    by a `cone` or `cylinder` line. For a circle drawn on the page use `on` instead —',
    '    `A = on c 100` — which measures from the x axis rather than from the front, and keeps',
    '    the point on the circle when the circle moves.',
    '  H = foot B CD  — the foot of the perpendicular from B to a line already named. Two',
    '    points instead of the name work as well, and on a solid that is the only form.',
    '  K = foot O S B  — the foot of the perpendicular from O to the line SB. On a solid it',
    '    is worked out in space; in a flat figure it is worked out on the page, which is the',
    '    same thing there. Use it in both. `project` is the page one and only the page one, so',
    '    on a solid it lands slightly off: a projection does not preserve a right angle.',
    '',
    '  **Khoảng cách từ một điểm đến một mặt phẳng** is two `foot`s, not a missing statement.',
    '  There is no `foot` onto a *plane*, and there does not need to be: the construction a',
    '  textbook draws is the one to write down. For d(O, (SAB)) where the apex stands over the',
    '  base and O is in it —',
    '    K = foot O A B     # in the base: drop O onto the edge AB',
    '    SK = segment S K',
    '    H = foot O S K     # then onto SK, which is the line of steepest ascent of (SAB)',
    '    OH = segment O H   # OH ⊥ (SAB), and |OH| is the distance',
    '  Draw `OK` and `SK` too, or the second foot has nothing to hang on. This is worth reading',
    '  before deciding the language cannot express a problem: asked for exactly this, one script',
    '  wrote the two lines above and another gave up and left a note saying `foot` only reaches a',
    '  line — the first was right.',
    '',
    'A problem about a function — recognise one and draw it as a **graph**:',
    '  A statement naming `f(x)`, `y = ...`, an interval like [-1;5], a monotonic table, roots,',
    '  extrema or an area under a curve is a graph problem. Draw it with `axes` and `graph`,',
    '  never by joining points with `segment`: a run of straight pieces between dots is not the',
    '  curve of a continuous function, and it cannot be edited afterwards.',
    '  ```',
    '  C = graph "x^2-4x+3" -1 5   # the curve, over the interval the problem gives',
    '  marks C                     # its vertex, its roots and where it meets Oy',
    '  variation C                 # the bảng biến thiên, under the figure',
    '  t = tangent C 2             # the tiếp tuyến at x = 2',
    '  label C "(C)"',
    '  ```',
    '  `tangent` takes an **abscissa** here, not a point: `tangent C 2` is the tangent to the',
    '  curve where x = 2, computed from the derivative. `tangent c A` — with a name — is still',
    '  the tangent to a circle from an external point; the two are told apart by the argument.',
    '',
    'A problem about a system of inequalities — shade it with **region**:',
    '  `region "x >= 0" "y >= 0" "2x + y <= 4"` draws the boundaries and shades the set of',
    '  points satisfying all of them. A strict inequality gets a dashed boundary, which is the',
    '  difference between `<` and `<=` and not a matter of taste. Each argument is one quoted',
    '  linear inequality in `x` and `y`; anything else is refused rather than approximated, so',
    '  `"x^2 + y <= 1"` is an error and not a wrong picture. Axes are added to fit the region.',
    '  The frame is fitted to the curve, so the formula does not have to be shrunk to fit a',
    '  window: write the function the problem states. Axes are added to match if you omit them,',
    '  and every asymptote is drawn dashed by itself.',
    '  **Say what the question is about with `marks`, not with `plot`.** A figure the question',
    '  reads off — "tìm giá trị cực đại", "đồ thị cắt Ox tại mấy điểm" — needs the point dotted',
    '  and its coordinates written, and `marks` computes both from the formula. Working an',
    '  extremum out yourself and typing `plot -1 2` puts a coordinate on the page that nothing',
    '  checks against the curve under it. Ask for one kind when the question asks for one kind:',
    `  \`marks C extrema\`, and likewise ${Object.keys(MARK_GROUPS)
      .filter((name) => name !== 'extrema')
      .map((name) => `\`${name}\``)
      .join(', ')}. It refuses rather than crowd the figure past a dozen dots.`,
    '  What this produces is a **live graph**: the reader can retype the formula, drag the',
    '  range and take a variation table from it. That is worth far more than a picture, so',
    '  prefer it even when the problem shows the curve rather than stating it — pick the',
    '  simplest formula with the right shape (the right number of turning points, the right',
    '  signs, the stated roots) and say so in a `#` comment. A cubic covers most of them.',
    '  `graph` needs no separate axes line if you forget one: a pair is added to match.',
    '  Formulas are written as on a blackboard: `x^2`, `x^3-3x`, `1/x`, `sqrt(x)`, `abs(x)`,',
    '  `sin(x)`, `exp(x)`, `ln(x)`, `(x+1)/(x-1)`. There is one variable and it is `x`.',
    '',
    '  No statement takes a bracketed expression — name the object on its own line first.',
    '  A `line` is endless, so one built only to find a crossing runs off both sides of the',
    '  figure and reads as part of it. Build it, take the `intersect`, then `hide` it, and draw',
    '  the piece you meant as a `segment` — `hide lBC` then `CI = segment C I`.',
    '  M = divide A B 2 3  — two thirds of the way from A to B. A ratio along a line is',
    '    one of the few metric statements a projection does preserve.',
    '',
    /*
     * The marks, which are the half of a figure that carries the *problem*.
     *
     * Twelve problems put through a model produced twelve reasonable-looking figures and almost
     * no marks: a pyramid whose whole hypothesis is "SA ⊥ (ABCD)" drawn without a single square,
     * a cone with `anglemark` on the two angles the question turns on and no caption on either,
     * so the α and the 60° the reader needs were simply absent. The lines were right and the
     * figure did not state the problem.
     *
     * It is worth being blunt about here rather than hinting: a mark is not decoration a figure
     * can be finished without.
     */
    'Marking what the problem says — a figure without these does not state the problem:',
    '  Mark every angle the problem names, and caption it, or the reader cannot tell which',
    '  angle is meant: `alpha = anglemark S A O`, then `text alpha "α"`.',
    '  Mark every perpendicular the problem states — `SA ⊥ (ABCD)` means a mark at A between',
    '  SA and two base edges: `r1 = anglemark S A B` and `r2 = anglemark S A D`.',
    '  A right angle is drawn as a square automatically, and on a solid it is decided in',
    '  space, so mark it wherever the problem says it is even though the projected angle on',
    '  the page is not 90°. Do not skip it because the drawing looks wrong: SA ⊥ AD projects',
    '  to 60° in a cabinet view and is still marked square.',
    '  Give lengths the problem names with `text` on the segment — on a *drawn* one. A solid',
    '  names its vertices and not its edges, so `text AB "a"` is an unknown name until you',
    '  have written `AB = segment A B`. Draw it, then caption it.',
    '  A mark needs its two arms *drawn*. It marks the angle between two lines, so with',
    '  neither of them on the page it is ink attached to nothing — and a vertex that has been',
    '  `hide`n leaves not even a dot to hold it. To show a segment meeting a plane squarely,',
    '  draw the segment right up to the plane and mark it against an edge of that plane that',
    '  is drawn too.',
    '  An angle in projection is foreshortened, so a cramped one needs `anglemarkbig`, and',
    '  its caption is always the real value from the problem, never the angle on the page.',
    '',
    'Planes:',
    '  `p = plane S A C` — a plane through three of the figure’s points, drawn as the section',
    '    it cuts. The section is **worked out**, not typed: name the plane and the program',
    '    finds where it meets each edge, in the right order, and re-cuts it whenever the solid',
    '    moves. It arrives toned, so no `style` line is needed, and `label p "(SAC)"` names it',
    '    the way the problem does.',
    '  The solid is not written down — it is found from the points. Points belonging to two',
    '    different figures are an error rather than a guess.',
    '  `q = planepar M p` — through a point, parallel to a plane already named. “Mặt phẳng qua',
    '    M song song với (ABC)”.',
    '  `d = meet p q` — the giao tuyến, as the segment of it inside the solid. `(SAC)` and',
    '    `(SBD)` meet along the height of the pyramid, and this works that out; drawing `SO`',
    '    by hand means solving the problem before the figure can be drawn.',
    '  `H = foot S p` — the foot of the perpendicular from a point to a plane. Every',
    '    “khoảng cách từ S đến (ABCD)” is about this point, and the height of a pyramid is the',
    '    segment from the apex to it.',
    '  `measure "distance" S p` gives the real distance from a point to a plane, and',
    '    `measure "angle" p q` the angle *between two planes* — both worked out in space, so',
    '    they are the numbers the problem states and not the foreshortened ones on the page.',
    '    Parallel planes measure 0°, which is what makes “are these parallel?” a question',
    '    about a number.',
    '  On a **round** solid a plane cuts one of two shapes, and only these two. Square to the',
    '    axis — “mặt phẳng song song với đáy” — it cuts a *circle*, drawn as a real ellipse and',
    '    smooth at any magnification. Through the axis — “thiết diện qua trục” — it cuts a',
    '    triangle, a rectangle or a trapezium. Cut obliquely, a cylinder gives an ellipse and a',
    '    cone an ellipse, a parabola or a hyperbola, each truncated by the flat ends: a boundary',
    '    of arcs *and* chords that nothing here can express, so it is refused rather than',
    '    sampled into a polygon that would draw it flat and convincing and wrong.',
    '  Three points in a line name no plane, and a plane that only grazes a corner has no',
    '    section. Both come back as nothing drawn rather than as an invented answer.',
    '  Tone the plane the question is *about*, not every plane it names: two or three washes',
    '  in one figure leave a reader with no idea which one the answer concerns.',
    '',
    '  `p = polygon S A B` with `style p plane` still works and is still the way to shade a',
    '    region that is not a section — but for a section prefer `plane`, which cannot be',
    '    wrong: four typed corners that are nowhere near one plane shade just as convincingly',
    '    as four that are.',
    '',
    `Measure names: ${MEASURES.join(', ')}`,
    '',
    'Example:',
    ...EXAMPLE_SCRIPT.split('\n').map((line) => `  ${line}`),
  ];
  return lines.join('\n');
}
